import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import {Icon} from '@/components/icons/Icon';
import Spinner from '@/components/ui/Spinner';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import usePaginatedData from '@/utils/usePaginatedData';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import AudioAnonymizationTasksSettingsModal from './AudioAnonymizationTasksSettingsModal';
import {navigate} from '@reach/router';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import i18n from 'i18n';
import {ModalAlert} from '@/components/alerts';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface AudioAnonymizationTask {
    id: number;
    taskId: string;
    eventId: number;
    createdOn: string;
    expireOn: string;
    status: string | null;
}

interface UserData {
    permissions?: string[];
}


interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const AudioAnonymizationTasksListView: React.FC = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const deleteAudioAnonymizationTask = useStoreActions((actions: any) => actions.audioAnonymizationTasks.deleteOne);

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedAudioAnonymizationTaskId, setSelectedAudioAnonymizationTaskId] = useState<number>(0);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [taskToDelete, setTaskToDelete] = useState<{id: number, taskId: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const {
        data: audioAnonymizationTasks,
        loading: loadingAudioAnonymizationTasks,
        currentPage,
        setCurrentPage,
        pages,
    }: PaginatedDataResult<AudioAnonymizationTask> = usePaginatedData(window.URLS.audioAnonymizationTasksResource, {
        storeResolver: 'audioAnonymizationTasks',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const canDeleteAudioAnonymizationTask = hasPermission('fs.audio_anonymisation.task.delete', userData?.permissions, true);
    const canAccessCallDetail = hasPermission('fs.event.read', userData?.permissions, true)

    const userCanAccessAudioAnonymizationTasksResource = hasPermission('fs.audio_anonymisation.task.list', userData?.permissions, true);

    if (!userCanAccessAudioAnonymizationTasksResource && !loadingUserData) {
        return (
            <>
                <ConditionalNav>

                </ConditionalNav>

                <HelpTextBlock helpText={i18n.t('ht_the_audio.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (transcriptionTaskId: number): void => {
        setSelectedAudioAnonymizationTaskId(transcriptionTaskId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedAudioAnonymizationTaskId(0);
    };

    const active = currentPage;
    let items: React.ReactNode;

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const openDeleteConfirm = (id: number, taskId: string): void => {
        setTaskToDelete({id, taskId});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setTaskToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!taskToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = `${window.URLS.audioAnonymizationTasksResource}/${taskToDelete.id}`;
            await axios.delete(url);
            deleteAudioAnonymizationTask(taskToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, taskId: string): void => {
        openDeleteConfirm(id, taskId);
    };

    if (loadingUserData) {
        return (
            <Spinner
                size="md"
                text={i18n.t('jt_loading_data_general.text')}
                centered
                minHeight="400px"
            />
        );
    }

    const detailUrlGenerator = (rowData: AudioAnonymizationTask): string => {
        return buildEventDetailURL(rowData.eventId);
    };

    const columns = [
        {
            accessorKey: 'createdOn',
            header: i18n.t('th_date_created.text'),
            cell: (cell: any) => (
                <FormattedDateTime
                    dateTime={cell.row.original.createdOn}
                    showTime={true}
                    options={{setZone: false}}
                    fallback="-"
                />
            ),
        },
        {
            accessorKey: 'taskId',
            header: i18n.t('th_task.text'),
            cell: (cell: any) => (
                <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />
            ),
        },
        {
            accessorKey: 'expireOn',
            header: i18n.t('th_expires.text'),
            cell: (cell: any) => (
                <FormattedDateTime
                    dateTime={cell.row.original.expireOn}
                    showTime={true}
                    options={{setZone: false}}
                    fallback="-"
                />
            ),
        },
        {
            accessorKey: 'status',
            header: i18n.t('th_status.text'),
            cell: (cell: any) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <div className="d-flex align-items-center justify-content-end w-100">
                    <div className="d-inline-block mr-2">
                        {
                            canAccessCallDetail ? (
                                <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-eye"
                                        onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{
                                        display: 'inline-block',
                                        cursor: 'not-allowed',
                                    }}
                                    >
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            icon="mdi-eye"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="audio-anonymization-tasks-list-eye-outlinesecondary-btn" />
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block ml-2">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id="settings-dropdown-1"
                                data-cy="audio-anonymization-tasks-list-outlineinfo-btn"
                            />

                            <Dropdown.Menu align="end">
                                <Dropdown.Item
                                    onClick={() => openSettingsModal(cell.row.original.id)}
                                >
                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                </Dropdown.Item>
                                {
                                    canDeleteAudioAnonymizationTask ? (
                                        <Dropdown.Item
                                            onClick={() => deleteOne(cell.row.original.id, cell.row.original.taskId)}
                                        >
                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="audio-anonymization-tasks-list-btn">
                                                    <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <ConditionalNav>

            </ConditionalNav>

            <HelpTextBlock helpText={i18n.t('ht_the_audio.text')} />

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingAudioAnonymizationTasks}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="300px"
            >
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={audioAnonymizationTasks} columns={columns} />
                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />

                        {
                            settingsModalOpen ? (
                                <AudioAnonymizationTasksSettingsModal
                                    modalOpen={settingsModalOpen}
                                    closeModal={closeSettingsModal}
                                    activeAudioAnonymizationTaskId={selectedAudioAnonymizationTaskId}
                                />
                            ) : null
                        }
                    </div>
                </div>
            </LoadingWrapper>
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={taskToDelete ? i18n.t('es_are_you_sure_delete.text', {taskId: taskToDelete.taskId}) : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

export default AudioAnonymizationTasksListView; 